/*!
=========================================================
* Painel Toca da Onça — Central de Marketplaces (evolução do AvantPro)
=========================================================
* Motor API-first: 19 plataformas + 5 agentes de IA. Conectar = login do dono
* em cada plataforma; os agentes ativam quando a 1ª chave de API entrar.
* Plano completo documentado no Obsidian (sessão 04-07).
*/
import React, { useState, useEffect } from "react";
import { Card, CardBody, CardTitle, Button, Row, Col, Badge } from "reactstrap";
import { BRIDGE } from "config"; // fonte única (OKF)

const OURO = "#ffd700";

// Página oficial de cadastro/conexão de vendedor de cada plataforma
const LINKS = {
  "Mercado Livre": "https://developers.mercadolivre.com.br/",
  "Shopee": "https://seller.shopee.com.br/",
  "Amazon Brasil": "https://venda.amazon.com.br/",
  "Magalu": "https://parceiro.magalu.com/",
  "Americanas (B2W)": "https://vendas.americanasmarketplace.com.br/",
  "Carrefour": "https://www.mercadounico.com.br/carrefour/",
  "Dafiti": "https://www.dafiti.com.br/lp/quero-vender/",
  "Netshoes / Zattini": "https://www.netshoes.com.br/marketplace",
  "Renner Marketplace": "https://www.lojasrenner.com.br/marketplace",
  "Riachuelo": "https://www.riachuelo.com.br/marketplace",
  "C&A Market": "https://www.cea.com.br/marketplace",
  "Bagy": "https://bagy.com.br/",
  "TikTok Shop": "https://seller-br.tiktok.com/",
  "Instagram Shopping": "https://business.facebook.com/commerce/",
  "Facebook Shops": "https://business.facebook.com/commerce/",
  "AliExpress": "https://home.aliexpress.com/",
  "Shein": "https://sellercenter.shein.com/",
  "1688 / Alibaba": "https://www.alibaba.com/",
  "Temu": "https://seller.temu.com/",
};

const CATEGORIAS = [
  {
    titulo: "Grandes generalistas — 90% das vendas do Brasil",
    itens: [
      { nome: "Mercado Livre", icon: "fas fa-shopping-cart", cor: "#ffe600", ic: "#2d3277", comissao: "11–17%", sinc: "Catálogo, estoque, pedidos, NF-e, variações" },
      { nome: "Shopee", icon: "fas fa-shopping-bag", cor: "#ee4d2d", ic: "#fff", comissao: "14–20%", sinc: "Catálogo, campanhas, cupons, frete grátis" },
      { nome: "Amazon Brasil", icon: "fab fa-amazon", cor: "#ff9900", ic: "#111", comissao: "12–18%", sinc: "Catálogo, pedidos, FBA, Buy Box, preços" },
      { nome: "Magalu", icon: "fas fa-store", cor: "#0086ff", ic: "#fff", comissao: "13–19%", sinc: "Produtos, estoque, pedidos, NF, entregas" },
      { nome: "Americanas (B2W)", icon: "fas fa-shopping-basket", cor: "#e60014", ic: "#fff", comissao: "14–20%", sinc: "Americanas + Submarino + Shoptime (3 em 1)" },
      { nome: "Carrefour", icon: "fas fa-store-alt", cor: "#004e9e", ic: "#fff", comissao: "12–17%", sinc: "Catálogo, pedidos, retirada na loja" },
    ],
  },
  {
    titulo: "Especializados em moda — margem melhor pra você",
    itens: [
      { nome: "Dafiti", icon: "fas fa-tshirt", cor: "#f5325b", ic: "#fff", comissao: "18–25%", sinc: "Moda fem/masc/infantil, calçados, esporte" },
      { nome: "Netshoes / Zattini", icon: "fas fa-running", cor: "#2a2a2a", ic: "#fff", comissao: "18–24%", sinc: "Esporte, casual, tênis, calçados" },
      { nome: "Renner Marketplace", icon: "fas fa-tshirt", cor: "#e40521", ic: "#fff", comissao: "20–28%", sinc: "Moda média/alta, marcas consolidadas" },
      { nome: "Riachuelo", icon: "fas fa-tshirt", cor: "#0a3d91", ic: "#fff", comissao: "18–24%", sinc: "Moda popular, familiar, giro rápido" },
      { nome: "C&A Market", icon: "fas fa-tshirt", cor: "#00306a", ic: "#fff", comissao: "17–23%", sinc: "Moda básica, familiar, plus size" },
      { nome: "Bagy", icon: "fas fa-female", cor: "#ff4d88", ic: "#fff", comissao: "17–22%", sinc: "Moda íntima, praia, fitness" },
    ],
  },
  {
    titulo: "Social commerce — vende direto nas redes",
    itens: [
      { nome: "TikTok Shop", icon: "fab fa-tiktok", cor: "#010101", ic: "#fff", comissao: "5–8%", sinc: "Venda nos vídeos e lives. Moda é o top." },
      { nome: "Instagram Shopping", icon: "fab fa-instagram", cor: "#dc2743", ic: "#fff", comissao: "—", sinc: "Marca produto no post/reel, compra no app" },
      { nome: "Facebook Shops", icon: "fab fa-facebook", cor: "#1877f2", ic: "#fff", comissao: "—", sinc: "Catálogo integrado, venda local e nacional" },
    ],
  },
  {
    titulo: "Plataformas da China — importação",
    itens: [
      { nome: "AliExpress", icon: "fas fa-globe-asia", cor: "#e62e04", ic: "#fff", comissao: "—", sinc: "Importação de peças e revenda" },
      { nome: "Shein", icon: "fas fa-tshirt", cor: "#111", ic: "#fff", comissao: "—", sinc: "Fast fashion, catálogo enorme" },
      { nome: "1688 / Alibaba", icon: "fas fa-industry", cor: "#ff6a00", ic: "#fff", comissao: "—", sinc: "Atacado direto da fábrica (B2B)" },
      { nome: "Temu", icon: "fas fa-gift", cor: "#fb7701", ic: "#fff", comissao: "—", sinc: "Preço baixo, giro rápido" },
    ],
  },
];

// Os 5 agentes autônomos (evolução do AvantPro) — ativam quando a 1ª API conectar
const AGENTES = [
  { nome: "Scout — Caçador de Tendências", icon: "fas fa-binoculars", cor: "#9b59b6", faz: "Cruza TikTok/Instagram com buscas dos marketplaces pra prever o próximo produto campeão ANTES da concorrência." },
  { nome: "Repricer — Estrategista de Preços", icon: "fas fa-tags", cor: "#e67e22", faz: "Ajusta seus preços 24/7 com limite mínimo de lucro, pra ganhar a Buy Box e a 1ª página." },
  { nome: "SEO — Copywriter de Anúncios", icon: "fas fa-pen-nib", cor: "#1abc9c", faz: "Acha títulos com pouca concorrência e gera título/descrição otimizados pro algoritmo de cada plataforma." },
  { nome: "CS — Atendente de Perguntas", icon: "fas fa-comments", cor: "#3498db", faz: "Responde perguntas de compradores no ML/Shopee em segundos, com o contexto do anúncio (irmão da Carmen)." },
  { nome: "Ads — Gerente de Campanhas", icon: "fas fa-bullhorn", cor: "#e74c3c", faz: "Lê os relatórios diários e realoca o orçamento de anúncios pros produtos com maior retorno." },
];

// O que o motor extrai de cada conexão (inteligência profunda, API-first)
const EXTRAI = [
  { icon: "fas fa-trophy", t: "Produtos campeões" },
  { icon: "fas fa-chart-line", t: "Vendas & conversão" },
  { icon: "fas fa-industry", t: "Fornecedor por trás" },
  { icon: "fas fa-map-marked-alt", t: "Cidades que mais compram" },
  { icon: "fas fa-list", t: "Catálogo completo do nicho" },
];

function CardMkt({ m }) {
  return (
    <Col lg="4" md="6">
      <Card style={{ minHeight: "196px", transition: "border .2s" }}
        onMouseEnter={(e) => (e.currentTarget.style.border = "1px solid rgba(255,215,0,.45)")}
        onMouseLeave={(e) => (e.currentTarget.style.border = "1px solid rgba(255,215,0,.07)")}>
        <CardBody>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: m.cor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className={m.icon} style={{ fontSize: 22, color: m.ic }} />
            </div>
            <div>
              <CardTitle tag="h4" className="text-white" style={{ margin: 0 }}>{m.nome}</CardTitle>
              {m.comissao !== "—" && <Badge color="warning" pill>Comissão {m.comissao}</Badge>}
            </div>
          </div>
          <p className="text-muted" style={{ minHeight: 40, fontSize: 13 }}>{m.sinc}</p>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Badge color="default" pill>○ Aguardando conexão</Badge>
            <Button color="info" size="sm" tag="a" target="_blank" rel="noreferrer" href={LINKS[m.nome] || "#"}>
              Conectar <i className="tim-icons icon-minimal-right" style={{ fontSize: 10 }} />
            </Button>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
}

// Card AO VIVO do 1º conector (Mercado Livre): lê o estado real do motor (bridge)
function ConectorML() {
  const [st, setSt] = useState(null); // null=carregando, {}=resposta, false=bridge off
  const puxa = () => fetch(BRIDGE + "/ml/status").then((r) => r.json()).then(setSt).catch(() => setSt(false));
  useEffect(() => { puxa(); const t = setInterval(puxa, 15000); return () => clearInterval(t); }, []);

  const conectar = () =>
    fetch(BRIDGE + "/ml/login").then((r) => r.json()).then((d) => {
      if (d.url) window.open(d.url, "_blank");
      else alert(d.erro || "Configure client_id/client_secret em ml.json primeiro.");
    }).catch(() => alert("Motor (bridge) desligado. Rode: node server.js na pasta toca-bridge."));

  const etapa = st && st.etapa;
  const cor = etapa === "conectado" ? "#00f2c3" : etapa === "pronto_pra_logar" ? OURO : "#888";
  const rotulo =
    st === false ? "Motor desligado — abra o bridge no PC" :
    st === null ? "Verificando o motor..." :
    etapa === "conectado" ? `Conectado como ${st.nickname || st.user_id}` :
    etapa === "pronto_pra_logar" ? "Chave instalada — falta você autorizar sua conta" :
    "Aguardando sua chave do DevCenter";

  return (
    <Card style={{ border: "1px solid rgba(255,215,0,.25)", background: "linear-gradient(135deg, rgba(255,230,0,.06), rgba(0,0,0,0) 60%)" }}>
      <CardBody>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ width: 46, height: 46, borderRadius: 12, background: "#ffe600", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="fas fa-shopping-cart" style={{ fontSize: 22, color: "#2d3277" }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <p className="text-white" style={{ margin: 0, fontWeight: 600 }}>
              Conector Mercado Livre <span style={{ color: OURO, fontSize: 12 }}>· 1º motor de API</span>
            </p>
            <p style={{ margin: 0, fontSize: 13, color: cor }}>
              <i className="tim-icons icon-bulb-63" style={{ marginRight: 6 }} />{rotulo}
            </p>
          </div>
          {etapa === "conectado" ? (
            <Button color="success" size="sm" onClick={puxa}>Atualizar</Button>
          ) : (
            <Button color="info" size="sm" onClick={conectar} disabled={etapa !== "pronto_pra_logar"}>
              Autorizar minha conta ML
            </Button>
          )}
        </div>
        {etapa === "aguardando_chave" && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: "#bbb", lineHeight: 1.7 }}>
            <b style={{ color: OURO }}>Como ligar (2 min, grátis):</b><br />
            1. Abra <a href="https://developers.mercadolivre.com.br/devcenter" target="_blank" rel="noreferrer" style={{ color: OURO }}>o DevCenter do Mercado Livre</a> e crie um app.<br />
            2. Em "URIs de redirect" cole: <code style={{ color: "#00f2c3" }}>https://painel-toca.pages.dev/ml-callback</code><br />
            3. Copie o <b>App ID</b> e a <b>Secret Key</b> num arquivo <code>chave-ml.txt</code> na Área de Trabalho — eu instalo e o motor acende.
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Marketplace() {
  const total = CATEGORIAS.reduce((s, c) => s + c.itens.length, 0);
  return (
    <div className="content">
      {/* HERO — central de inteligência */}
      <div style={{ background: "linear-gradient(135deg, rgba(255,215,0,.10) 0%, rgba(0,0,0,0) 55%)", border: "1px solid rgba(255,215,0,.25)", borderRadius: 16, padding: "22px 24px", marginBottom: 20 }}>
        <Row className="align-items-center">
          <Col md="8">
            <h3 style={{ color: OURO, fontWeight: 600, margin: 0 }}>
              <i className="fas fa-rocket" /> Central de Marketplaces
            </h3>
            <p className="text-muted" style={{ margin: "6px 0 0" }}>
              Motor de inteligência API-first — melhor que AvantPro: além de MOSTRAR os dados, os agentes AGEM por você.
            </p>
          </Col>
          <Col md="4" className="text-right">
            <div style={{ display: "flex", gap: 18, justifyContent: "flex-end" }}>
              <div style={{ textAlign: "center" }}>
                <p style={{ color: OURO, fontSize: 26, fontWeight: 700, margin: 0 }}>{total}</p>
                <p className="text-muted" style={{ fontSize: 11, margin: 0 }}>plataformas</p>
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{ color: "#00f2c3", fontSize: 26, fontWeight: 700, margin: 0 }}>{AGENTES.length}</p>
                <p className="text-muted" style={{ fontSize: 11, margin: 0 }}>agentes IA</p>
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{ color: "#fff", fontSize: 26, fontWeight: 700, margin: 0 }}>1</p>
                <p className="text-muted" style={{ fontSize: 11, margin: 0 }}>conectado (WhatsApp)</p>
              </div>
            </div>
          </Col>
        </Row>
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {EXTRAI.map((e, i) => (
            <span key={i} style={{ background: "rgba(255,215,0,.08)", border: "1px solid rgba(255,215,0,.25)", color: "#eee", borderRadius: 20, padding: "5px 14px", fontSize: 12 }}>
              <i className={e.icon} style={{ color: OURO, marginRight: 6 }} />{e.t}
            </span>
          ))}
        </div>
      </div>

      {/* CONECTOR AO VIVO — 1º motor de API (Mercado Livre) */}
      <Row>
        <Col xs="12"><ConectorML /></Col>
      </Row>

      {/* AGENTES DE IA */}
      <Row>
        <Col xs="12">
          <h4 className="text-white" style={{ fontWeight: 300, borderLeft: "3px solid " + OURO, paddingLeft: 12 }}>
            Agentes de IA da operação <Badge color="warning" pill style={{ marginLeft: 6 }}>ativam ao conectar a 1ª API</Badge>
          </h4>
        </Col>
      </Row>
      <Row>
        {AGENTES.map((a, i) => (
          <Col lg="4" md="6" key={i}>
            <Card style={{ minHeight: 150 }}>
              <CardBody style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: a.cor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={a.icon} style={{ fontSize: 16, color: "#fff" }} />
                  </div>
                  <p className="text-white" style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{a.nome}</p>
                </div>
                <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>{a.faz}</p>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* AS 19 PLATAFORMAS */}
      <Row>
        <Col xs="12">
          <h4 className="text-white" style={{ fontWeight: 300, borderLeft: "3px solid " + OURO, paddingLeft: 12, marginTop: 8 }}>
            Conectar plataformas <span className="text-muted" style={{ fontSize: 13 }}>({total} — o botão abre a página oficial; você loga, cria a chave, e eu ligo o motor)</span>
          </h4>
        </Col>
      </Row>
      {CATEGORIAS.map((cat, i) => (
        <div key={i}>
          <Row>
            <Col xs="12">
              <p className="text-muted" style={{ margin: "10px 0 8px", paddingLeft: 12, letterSpacing: .5, fontSize: 13, textTransform: "uppercase" }}>{cat.titulo}</p>
            </Col>
          </Row>
          <Row>
            {cat.itens.map((m, j) => <CardMkt m={m} key={j} />)}
          </Row>
        </div>
      ))}
    </div>
  );
}

export default Marketplace;
